// src/quality-management/components/CreateTaskModal.tsx
// Reescrito sobre o Modal genérico do UI kit (nova identidade)

import React, { useState, useEffect, useContext } from 'react';
import { CreateTaskRequest, OvertimeType, ActivityType, Task } from '../services/api/types';
import { useMutation } from '../hooks/useApi';
import { apiService } from '../services/api';
import { calculateWeeklyHours, calculateOvertimeHours, calculateOvertimePay, getOvertimeTypeOptions, WEEKLY_HOUR_LIMIT } from '../utils/overtimeCalculator';
import { predecessorMinStart, formatDayBR } from '../utils/taskSchedule';
import { AuthContext } from '../contexts/AuthAdapter';
import { Modal } from '../../shared/components/ui/Modal';
import { Button } from '../../shared/components/ui/Button';
import { Input } from '../../shared/components/ui/Input';
import { Textarea } from '../../shared/components/ui/Textarea';
import { Select } from '../../shared/components/ui/Select';

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface CreateTaskModalProps {
  projectId: number;
  selectedDate: Date | null;
  assignedEmployeeIds: number[];
  onClose: () => void;
  onTaskCreated: () => void;
  availableEmployees?: { id: number; name: string }[];
}

const FORM_ID = 'create-task-form';

// Converte Date para YYYY-MM-DD.
// Usa os componentes UTC porque as datas chegam de duas origens: strings
// 'YYYY-MM-DD' (interpretadas como meia-noite UTC) e dias do calendário
// (meia-noite local). Ler os componentes locais deslocaria o primeiro caso um
// dia para trás em fusos negativos, criando a tarefa no dia errado.
const toISODate = (d: Date) => d.toISOString().split('T')[0];

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  projectId,
  selectedDate,
  assignedEmployeeIds,
  onClose,
  onTaskCreated,
  availableEmployees = [],
}) => {
  const { user } = useAuth();
  const [taskName, setTaskName] = useState('');
  const [description, setDescription] = useState('');
  const [expectedHours, setExpectedHours] = useState<number>(8);
  const [completionPercentage, setCompletionPercentage] = useState<number>(0);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [activityTypeId, setActivityTypeId] = useState<string>('');
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [predecessorId, setPredecessorId] = useState<string>('');
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [taskDateStr, setTaskDateStr] = useState<string>(() =>
    selectedDate ? toISODate(selectedDate) : ''
  );
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    apiService.activityTypes
      .getProjectActivityTypes(projectId)
      .then((types) => {
        if (active) setActivityTypes(Array.isArray(types) ? types : []);
      })
      .catch((e) => console.error('Error fetching activity types:', e));
    return () => {
      active = false;
    };
  }, [projectId]);

  // Tarefas do projeto para o vínculo de predecessora
  useEffect(() => {
    let active = true;
    apiService.tasks
      .getTasks({ project_id: projectId })
      .then((ts) => {
        if (active) setProjectTasks(Array.isArray(ts) ? ts : []);
      })
      .catch((e) => console.error('Error fetching project tasks:', e));
    return () => {
      active = false;
    };
  }, [projectId]);

  useEffect(() => {
    if (selectedDate) setTaskDateStr(toISODate(selectedDate));
  }, [selectedDate]);

  const selectedPredecessor = projectTasks.find((t) => String(t.id) === predecessorId) || null;
  const minStart = selectedPredecessor ? predecessorMinStart(selectedPredecessor) : null;

  // Ao vincular uma predecessora, a data de início vai automaticamente para
  // um dia após a conclusão planejada dela (o usuário pode adiar, nunca antecipar)
  const handlePredecessorChange = (value: string) => {
    setPredecessorId(value);
    const pred = projectTasks.find((t) => String(t.id) === value);
    const min = pred ? predecessorMinStart(pred) : null;
    if (min && (!taskDateStr || taskDateStr < min)) setTaskDateStr(min);
  };

  // Pré-seleção de funcionários, aplicada uma única vez assim que houver dados
  // (o usuário chega de forma assíncrona, então não dá para depender só do mount).
  // Ordem: 1) funcionário logado; 2) atribuição vinda de quem abriu o modal
  // (ex.: linha clicada no calendário); 3) único funcionário disponível.
  // Depois disso a escolha manual nunca é sobrescrita por um re-render.
  const preselectedRef = React.useRef(false);
  useEffect(() => {
    if (preselectedRef.current) return;

    if (user?.role === 'Employee' && user.employee_id) {
      preselectedRef.current = true;
      setSelectedEmployees([user.employee_id]);
    } else if (assignedEmployeeIds && assignedEmployeeIds.length > 0) {
      preselectedRef.current = true;
      setSelectedEmployees(assignedEmployeeIds);
    } else if (availableEmployees.length === 1) {
      preselectedRef.current = true;
      setSelectedEmployees([availableEmployees[0].id]);
    }
  }, [user, assignedEmployeeIds, availableEmployees]);

  const [currentWeeklyHours, setCurrentWeeklyHours] = useState<number>(0);
  const [hasOvertime, setHasOvertime] = useState<boolean>(false);
  const [overtimeHours, setOvertimeHours] = useState<number>(0);
  const [overtimeType, setOvertimeType] = useState<OvertimeType>('regular_overtime');
  const [overtimeJustification, setOvertimeJustification] = useState<string>('');
  const [, setLoadingWeeklyTasks] = useState<boolean>(false);

  const { mutate: createTask, loading: isSubmitting } = useMutation(
    (data: CreateTaskRequest & { assigned_employee_ids: number[] }) =>
      apiService.tasks.createTask(projectId, { ...data, assigned_employee_ids: data.assigned_employee_ids }),
    {
      onSuccess: () => {
        onTaskCreated();
        onClose();
      },
      onError: (error) => {
        setError(error.message || 'Erro ao criar tarefa. Tente novamente.');
      },
    }
  );

  useEffect(() => {
    const fetchWeeklyTasks = async () => {
      if (!selectedDate || !user) return;
      setLoadingWeeklyTasks(true);
      try {
        const weekStart = getWeekStart(selectedDate);
        const allTasks = await apiService.tasks.getTasks();
        const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
        const userWeeklyTasks = allTasks.filter((task: any) => {
          const taskDate = new Date(task.task_date + 'T00:00:00');
          const isInWeek = taskDate >= weekStart && taskDate <= weekEnd;
          const isAssignedToUser = task.assigned_employee_ids && task.assigned_employee_ids.includes(user?.employee_id);
          return isInWeek && isAssignedToUser;
        });
        const weeklyHours = calculateWeeklyHours(userWeeklyTasks, selectedDate);
        setCurrentWeeklyHours(weeklyHours);
      } catch (error: any) {
        console.error('Error fetching weekly tasks:', error);
      } finally {
        setLoadingWeeklyTasks(false);
      }
    };
    fetchWeeklyTasks();
  }, [selectedDate, user]);

  useEffect(() => {
    if (!selectedDate) return;
    const potentialOvertimeHours = calculateOvertimeHours(currentWeeklyHours, expectedHours);
    const willHaveOvertime = potentialOvertimeHours > 0;
    setHasOvertime(willHaveOvertime);
    setOvertimeHours(potentialOvertimeHours);
    if (!willHaveOvertime) {
      setOvertimeType('regular_overtime');
      setOvertimeJustification('');
    }
  }, [currentWeeklyHours, expectedHours, selectedDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskName.trim()) return setError('Nome da tarefa é obrigatório');
    if (expectedHours <= 0) return setError('Horas previstas devem ser maiores que 0');
    if (completionPercentage < 0 || completionPercentage > 100) return setError('Percentual de conclusão deve estar entre 0 e 100');
    if (!taskDateStr) return setError('Data é obrigatória');
    if (minStart && taskDateStr < minStart)
      return setError(`A data de início deve ser a partir de ${formatDayBR(minStart)}, um dia após a conclusão da predecessora`);
    if (hasOvertime && !overtimeJustification.trim()) return setError('Justificativa de horas extras é obrigatória');
    if (selectedEmployees.length === 0) return setError('Selecione ao menos um funcionário');

    setError('');
    await createTask({
      name: taskName.trim(),
      description: description.trim() || undefined,
      expected_hours: expectedHours,
      completion_percentage: completionPercentage,
      task_date: taskDateStr,
      predecessor_id: predecessorId ? Number(predecessorId) : null,
      activity_type_id: activityTypeId ? Number(activityTypeId) : null,
      assigned_employee_ids: selectedEmployees,
      has_overtime: hasOvertime,
      overtime_hours: hasOvertime ? overtimeHours : 0,
      overtime_type: hasOvertime ? overtimeType : undefined,
      overtime_justification: hasOvertime ? overtimeJustification.trim() : undefined,
    });
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const getWeekStart = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const formatSelectedDate = () => {
    if (!taskDateStr) return '';
    return new Date(`${taskDateStr}T00:00:00`).toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Modal
      isOpen
      onClose={handleClose}
      title="Nova tarefa"
      size="lg"
      closeOnBackdrop={!isSubmitting}
      data-testid="create-task-modal"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" form={FORM_ID} loading={isSubmitting} disabled={isSubmitting || !taskName.trim()} data-testid="task-save">
            Criar tarefa
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-5">
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3.5 py-2.5 text-sm text-danger">
            {error}
          </div>
        )}

        {taskDateStr && (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3.5 py-2.5 text-sm text-foreground-muted">
            <span className="font-medium text-foreground">Data de início:</span>
            <span className="capitalize">{formatSelectedDate()}</span>
          </div>
        )}

        {/* Vínculo de predecessora e data de início */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            id="task-predecessor"
            name="task-predecessor"
            label="Atividade predecessora"
            placeholder="Sem predecessora"
            value={predecessorId}
            onChange={(e) => handlePredecessorChange(e.target.value)}
            disabled={isSubmitting}
            data-testid="task-predecessor-select"
            options={projectTasks.map((t) => ({ value: t.id, label: t.name || t.title || `Tarefa ${t.id}` }))}
          />
          <Input
            id="task-date"
            name="task-date"
            type="date"
            label="Data de início *"
            value={taskDateStr}
            min={minStart ?? undefined}
            onChange={(e) => setTaskDateStr(e.target.value)}
            required
            disabled={isSubmitting}
            data-testid="task-date-input"
          />
        </div>

        {selectedPredecessor && minStart && (
          <div className="rounded-lg border border-brand/20 bg-brand/10 px-3.5 py-2.5 text-sm text-foreground">
            Esta tarefa depende de <strong>{selectedPredecessor.name || selectedPredecessor.title}</strong>.
            O início mais cedo permitido é <strong>{formatDayBR(minStart)}</strong>, um dia após a conclusão
            planejada da predecessora.
          </div>
        )}

        {user && user.role === 'Employee' && (
          <div className="rounded-lg border border-brand/20 bg-brand/10 px-3.5 py-2.5 text-sm text-foreground">
            Esta tarefa será atribuída a <strong>{user.name}</strong>
          </div>
        )}

        {user && user.role !== 'Employee' && availableEmployees.length > 0 && (
          availableEmployees.length === 1 ? (
            <div className="rounded-lg border border-success/20 bg-success/10 px-3.5 py-2.5 text-sm text-foreground">
              Esta tarefa será atribuída a <strong>{availableEmployees[0].name}</strong>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground">Atribuir a funcionários *</span>
              <div className="max-h-44 overflow-y-auto rounded-lg border border-border">
                {availableEmployees.map((employee) => (
                  <label
                    key={employee.id}
                    htmlFor={`employee-${employee.id}`}
                    className="flex cursor-pointer items-center gap-3 border-b border-border/60 px-3.5 py-2.5 text-base text-foreground last:border-0 hover:bg-surface-2"
                  >
                    <input
                      type="checkbox"
                      id={`employee-${employee.id}`}
                      checked={selectedEmployees.includes(employee.id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedEmployees((prev) => [...prev, employee.id]);
                        else setSelectedEmployees((prev) => prev.filter((id) => id !== employee.id));
                      }}
                      disabled={isSubmitting}
                      className="h-4 w-4 accent-brand"
                    />
                    {employee.name}
                  </label>
                ))}
              </div>
              {selectedEmployees.length === 0 && <span className="text-sm text-danger">Selecione ao menos um funcionário</span>}
            </div>
          )
        )}

        <Input
          id="task-name"
          name="task-name"
          label="Nome da tarefa *"
          placeholder="Digite o nome da tarefa"
          value={taskName}
          onChange={(e) => setTaskName(e.target.value)}
          required
          disabled={isSubmitting}
          data-testid="task-name-input"
        />

        <Textarea
          id="task-description"
          name="task-description"
          label="Descrição"
          placeholder="Descreva os objetivos e requisitos da tarefa"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          disabled={isSubmitting}
        />

        {activityTypes.length > 0 && (
          <Select
            id="task-activity-type"
            name="task-activity-type"
            label="Tipo de atividade"
            placeholder="Sem tipo"
            value={activityTypeId}
            onChange={(e) => setActivityTypeId(e.target.value)}
            disabled={isSubmitting}
            data-testid="task-activity-type-select"
            options={activityTypes.map((at) => ({ value: at.id, label: at.name }))}
          />
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            id="expected-hours"
            name="expected-hours"
            label="Horas previstas *"
            type="number"
            min="0.5"
            step="0.5"
            value={expectedHours}
            onChange={(e) => setExpectedHours(parseFloat(e.target.value) || 0)}
            required
            disabled={isSubmitting}
            data-testid="expected-hours-input"
          />
          <Input
            id="completion-percentage"
            name="completion-percentage"
            label="Conclusão %"
            type="number"
            min="0"
            max="100"
            step="5"
            value={completionPercentage}
            onChange={(e) => setCompletionPercentage(parseInt(e.target.value) || 0)}
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">Progresso: {completionPercentage}%</span>
          <div className="h-2 overflow-hidden rounded-full bg-surface-3">
            <div
              className={completionPercentage === 100 ? 'h-full rounded-full bg-success' : 'h-full rounded-full bg-brand'}
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>

        {hasOvertime && (
          <div className="flex flex-col gap-4 rounded-xl border border-warning/30 bg-warning/10 p-4">
            <div className="flex items-center gap-2 text-warning">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <path d="M12 9v4M12 17h.01" />
              </svg>
              <span className="font-semibold">Horas extras detectadas</span>
            </div>
            <div className="text-sm text-foreground-muted">
              <p>
                Semana atual: {currentWeeklyHours.toFixed(1)}h + Nova tarefa: {expectedHours}h ={' '}
                {(currentWeeklyHours + expectedHours).toFixed(1)}h
              </p>
              <p>
                Horas extras: <strong className="text-foreground">{overtimeHours.toFixed(1)}h</strong> (excede o limite de {WEEKLY_HOUR_LIMIT}h/semana)
              </p>
            </div>

            <Select
              id="overtime-type"
              name="overtime-type"
              label="Tipo de hora extra *"
              value={overtimeType}
              onChange={(e) => setOvertimeType(e.target.value as OvertimeType)}
              required
              disabled={isSubmitting}
              options={getOvertimeTypeOptions().map((o) => ({ value: o.value, label: o.label }))}
              data-testid="overtime-type-input"
            />

            <Textarea
              id="overtime-justification"
              name="overtime-justification"
              label="Justificativa *"
              placeholder="Explique por que esta hora extra é necessária..."
              value={overtimeJustification}
              onChange={(e) => setOvertimeJustification(e.target.value)}
              rows={3}
              required
              disabled={isSubmitting}
              data-testid="overtime-justification"
            />

            <div className="rounded-lg bg-surface-2 px-3.5 py-2.5 text-sm font-medium text-foreground">
              Pagamento estimado: {calculateOvertimePay(overtimeHours, overtimeType).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default CreateTaskModal;
